import { ArcGISIdentityManager } from '@esri/arcgis-rest-request';
import type { IOAuth2Options, IUser } from '@esri/arcgis-rest-request';
import { getSelf } from '@esri/arcgis-rest-portal';
import { buildRedirectUri, normalizePortalUrl, requireClientId } from './config.js';
import {
  ARCGIS_ACCOUNT_STORAGE_KEY,
  ARCGIS_AUTH_BROADCAST_CHANNEL,
  clearPendingOAuthRequest,
  createAccountId,
  isStoredAccountExpired,
  loadActiveAccount,
  loadPendingOAuthRequest,
  removeStoredAccount,
  saveActiveAccount,
  savePendingOAuthRequest,
  type ArcgisAuthProvider,
  type BrowserStorage,
  type PendingOAuthRequest,
  type StoredArcgisAccount,
} from './storage.js';

export interface BeginSignInOptions {
  provider: ArcgisAuthProvider;
  portalUrl: string;
  clientId: string;
  basePath?: string;
  callbackPath?: string;
  expirationMinutes?: number;
}

export interface OAuthWindow {
  location: Location;
  localStorage: BrowserStorage;
  addEventListener: Window['addEventListener'];
  dispatchEvent: Window['dispatchEvent'];
  open: Window['open'];
  close: Window['close'];
  history: History;
  opener?: Window | null;
  crypto: Crypto;
  isSecureContext: boolean;
  TextEncoder: typeof TextEncoder;
  removeEventListener: Window['removeEventListener'];
}

export function hasOAuthResponse(location: Pick<Location, 'search' | 'hash'>): boolean {
  return /[?&](code|error)=/.test(location.search) || /[#&](access_token|error)=/.test(location.hash);
}

export function isOAuthCallbackRoute(
  location: Pick<Location, 'pathname'>,
  callbackPath = '/auth/callback',
): boolean {
  const normalizedPath = normalizeRoutePath(location.pathname);
  const normalizedCallbackPath = normalizeRoutePath(callbackPath);
  return normalizedPath === normalizedCallbackPath || normalizedPath.endsWith(normalizedCallbackPath);
}

export function createOAuthRequest(options: BeginSignInOptions, origin: string): PendingOAuthRequest {
  const portalUrl = normalizePortalUrl(options.portalUrl);
  const clientId = requireClientId(options.clientId);
  return {
    provider: options.provider,
    portalUrl,
    clientId,
    redirectUri: buildRedirectUri(origin, options.basePath, options.callbackPath),
    expirationMinutes: options.expirationMinutes ?? 20_160,
    startedAt: new Date().toISOString(),
  };
}

export async function beginArcgisSignIn(
  options: BeginSignInOptions,
  win = window as unknown as OAuthWindow,
): Promise<StoredArcgisAccount | undefined> {
  const pendingRequest = createOAuthRequest(options, win.location.origin);
  savePendingOAuthRequest(pendingRequest, win.localStorage);
  const storedCompletionWait = createStoredSignInCompletionWait(pendingRequest, win);

  try {
    const account = await Promise.race([
      ArcGISIdentityManager.beginOAuth2(createOAuthOptions(pendingRequest), win as unknown as Window).then(async (manager) => {
        if (!manager) return undefined;

        const account = await buildStoredAccount(manager, pendingRequest.provider, pendingRequest.portalUrl, pendingRequest.clientId);
        saveActiveAccount(account, win.localStorage);
        return account;
      }),
      storedCompletionWait.promise,
    ]);

    if (!account) {
      // IMPORTANT: do NOT clear the pending request here. In the
      // same-window flow (`popup: false`) `ArcGISIdentityManager.beginOAuth2`
      // synchronously sets `window.location.href = authorizeUrl` and
      // resolves to `undefined` *before* the browser actually navigates
      // (see node_modules/@esri/arcgis-rest-request/.../ArcGISIdentityManager.js
      // around `win.location.href = authorizeUrl; return undefined;`).
      // If we cleared `localStorage` here, the post-redirect call to
      // `completeArcgisSignIn` would find no pending request and
      // silently abort, leaving the user on a near-blank page after
      // sign-in. The pending request is cleared by `completeArcgisSignIn`
      // (in its `finally` block) once the redirect resolves.
      return undefined;
    }

    clearPendingOAuthRequest(win.localStorage);
    return account;
  } catch (error) {
    clearPendingOAuthRequest(win.localStorage);
    throw error;
  } finally {
    storedCompletionWait.cancel();
  }
}

export function isStoredOAuthCompletionAccount(account: StoredArcgisAccount, request: PendingOAuthRequest): boolean {
  return (
    account.provider === request.provider &&
    account.portalUrl === request.portalUrl &&
    account.clientId === request.clientId &&
    Date.parse(account.updatedAt) >= Date.parse(request.startedAt)
  );
}

export async function completeArcgisSignIn(win = window as unknown as OAuthWindow): Promise<StoredArcgisAccount | undefined> {
  const pendingRequest = loadPendingOAuthRequest(win.localStorage);
  if (!pendingRequest) return undefined;

  let manager: ArcGISIdentityManager | undefined;
  try {
    manager = await ArcGISIdentityManager.completeOAuth2(createOAuthOptions(pendingRequest), win as unknown as Window);
  } finally {
    clearPendingOAuthRequest(win.localStorage);
  }

  if (!manager) return undefined;

  const account = await buildStoredAccount(manager, pendingRequest.provider, pendingRequest.portalUrl, pendingRequest.clientId);
  saveActiveAccount(account, win.localStorage);
  return account;
}

export async function restoreActiveArcgisAccount(storage = globalThis.localStorage): Promise<StoredArcgisAccount | undefined> {
  const account = loadActiveAccount(storage);
  if (!account) return undefined;

  if (isStoredAccountExpired(account)) {
    removeStoredAccount(account.id, storage);
    return undefined;
  }

  let manager: ArcGISIdentityManager;
  try {
    manager = deserializeArcgisIdentity(account.serializedSession);
  } catch {
    // The stored session is corrupt/unreadable, so it can never be used.
    // Dropping it is safe and the only sensible recovery.
    removeStoredAccount(account.id, storage);
    return undefined;
  }

  try {
    const refreshedAccount = await buildStoredAccount(manager, account.provider, account.portalUrl, account.clientId);
    saveActiveAccount(refreshedAccount, storage);
    return refreshedAccount;
  } catch (error) {
    // Only discard the stored session when the portal *definitively*
    // rejects the credentials (invalid/expired/revoked token, access
    // denied). A transient failure — offline, a CORS hiccup, or a fetch
    // aborted because the page was navigating (net::ERR_ABORTED right
    // after the OAuth redirect) surfaces as a generic `TypeError:
    // Failed to fetch`. Deleting a still-valid, non-expired session on
    // those errors bounces a freshly signed-in user straight back to the
    // auth gate. In that case we keep the cached account and return it so
    // the user stays signed in; the next online interaction will refresh.
    if (isCredentialRejectedError(error)) {
      removeStoredAccount(account.id, storage);
      return undefined;
    }
    return account;
  }
}

/**
 * The arcgis-rest-js error `name` values that signal the portal has
 * *definitively* rejected the stored credentials (token invalid/expired/
 * revoked, or an access-denied OAuth response). Matching on `name` is the
 * pattern the library documents (e.g. `e.name === 'ArcGISAuthError'`); the
 * concrete error classes are not all re-exported from the package's type
 * surface, so a string match is the portable check.
 */
const CREDENTIAL_REJECTED_ERROR_NAMES = new Set([
  'ArcGISAuthError',
  'ArcGISTokenRequestError',
  'ArcGISAccessDeniedError',
]);

/**
 * Distinguishes a definitive portal rejection of the stored credentials
 * from a transient network/abort failure. Only the former should
 * invalidate a stored, not-yet-expired session.
 */
function isCredentialRejectedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = (error as { name?: unknown }).name;
  return typeof name === 'string' && CREDENTIAL_REJECTED_ERROR_NAMES.has(name);
}

export async function signOutArcgisAccount(account: StoredArcgisAccount, storage = globalThis.localStorage): Promise<void> {
  removeStoredAccount(account.id, storage);

  try {
    const manager = deserializeArcgisIdentity(account.serializedSession);
    await ArcGISIdentityManager.destroy(manager);
  } catch {
    // Local sign-out is authoritative for this app; revocation can fail for expired tokens or unreachable portals.
  }
}

export function deserializeArcgisIdentity(serializedSession: string): ArcGISIdentityManager {
  return ArcGISIdentityManager.deserialize(serializedSession);
}

export async function buildStoredAccount(
  manager: ArcGISIdentityManager,
  provider: ArcgisAuthProvider,
  portalUrl: string,
  clientId: string,
): Promise<StoredArcgisAccount> {
  const [user, portal] = await Promise.all([
    manager.getUser() as Promise<IUser>,
    getSelf({ authentication: manager }) as Promise<{ id?: string; name?: string }>,
  ]);

  const thumbnailDataUrl = await fetchUserThumbnailDataUrl(manager, portalUrl, user).catch(() => undefined);

  return {
    id: createAccountId(provider, portalUrl, user.username),
    provider,
    portalUrl,
    portalName: portal.name,
    clientId,
    username: user.username,
    fullName: user.fullName,
    thumbnailDataUrl,
    orgId: user.orgId ?? portal.id,
    orgName: portal.name,
    serializedSession: manager.serialize(),
    tokenExpiresAt: manager.tokenExpires?.toISOString(),
    refreshTokenExpiresAt: manager.refreshTokenExpires?.toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

async function fetchUserThumbnailDataUrl(
  manager: ArcGISIdentityManager,
  portalUrl: string,
  user: IUser,
): Promise<string | undefined> {
  if (!user?.thumbnail || !user.username) return undefined;
  const token = await manager.getToken(portalUrl);
  if (!token) return undefined;
  const trimmedPortal = portalUrl.replace(/\/+$/, '');
  const thumbnailUrl =
    `${trimmedPortal}/community/users/${encodeURIComponent(user.username)}` +
    `/info/${encodeURIComponent(user.thumbnail)}?token=${encodeURIComponent(token)}`;
  const response = await fetch(thumbnailUrl);
  if (!response.ok) return undefined;
  const blob = await response.blob();
  if (blob.size === 0 || blob.size > 256 * 1024) return undefined;
  return await new Promise<string | undefined>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      resolve(typeof result === 'string' ? result : undefined);
    };
    reader.onerror = () => resolve(undefined);
    reader.readAsDataURL(blob);
  });
}

function createOAuthOptions(request: PendingOAuthRequest): IOAuth2Options {
  return {
    clientId: request.clientId,
    portal: request.portalUrl,
    redirectUri: request.redirectUri,
    expiration: request.expirationMinutes,
    // Same-window redirect flow (matches the ArcGIS Assistant experience
    // and works in environments that block popups). `beginOAuth2` sets
    // `window.location.href` to the authorization URL; the portal then
    // redirects back to `redirectUri`, where the app bootstrap detects
    // the OAuth response and calls `completeOAuth2`.
    popup: false,
    pkce: true,
  };
}

function createStoredSignInCompletionWait(
  request: PendingOAuthRequest,
  win: OAuthWindow,
): { promise: Promise<StoredArcgisAccount>; cancel: () => void } {
  let channel: BroadcastChannel | undefined;
  let settled = false;
  let resolveCompletion: (account: StoredArcgisAccount) => void = () => undefined;

  const cleanup = (): void => {
    win.removeEventListener('storage', handleStorage);
    channel?.close();
  };

  const resolveIfMatchingAccount = (): void => {
    if (settled) return;
    const account = loadActiveAccount(win.localStorage);
    if (!account || !isStoredOAuthCompletionAccount(account, request)) return;
    settled = true;
    cleanup();
    resolveCompletion(account);
  };

  const handleStorage = (event: StorageEvent): void => {
    if (event.key !== ARCGIS_ACCOUNT_STORAGE_KEY) return;
    resolveIfMatchingAccount();
  };

  const handleBroadcastMessage = (event: MessageEvent): void => {
    if ((event.data as { type?: string } | undefined)?.type !== 'arcgis-auth-complete') return;
    resolveIfMatchingAccount();
  };

  const promise = new Promise<StoredArcgisAccount>((resolve) => {
    resolveCompletion = resolve;
    win.addEventListener('storage', handleStorage);
    if ('BroadcastChannel' in globalThis) {
      channel = new BroadcastChannel(ARCGIS_AUTH_BROADCAST_CHANNEL);
      channel.addEventListener('message', handleBroadcastMessage);
    }
  });

  return {
    promise,
    cancel: () => {
      if (settled) return;
      settled = true;
      cleanup();
    },
  };
}

function normalizeRoutePath(pathValue: string): string {
  const normalizedPath = `/${pathValue.replace(/^\/+|\/+$/g, '')}`;
  return normalizedPath === '/' ? '' : normalizedPath;
}
